/**
 * Параметры Яндекс.Директ: разобранные UTM-метки регистрации в фильтрах и группировках
 * ОТЧЁТА РЕКЛАМЫ (`/users/ad-reports`). В остальных отчётах и на дашбордах не показываются.
 *
 * Маркетологи пакуют параметры рекламы в одну UTM-метку регистрации
 * (`1sn|yd|2sn|89536426|...`), бэкенд её распаковывает и отдаёт справочник
 * GET /utm-params/sections: какие рекламные системы, параметры и кабинеты доступны школе.
 *
 * Состав секций динамический — на фронте ничего не хардкодится: дерево фильтров и дерево
 * группировок достраиваются из справочника (по образцу `injectRegistrationGroupsNode`
 * для «Групп регистраций»). Узел секции живёт в сущности «Реклама» сразу после
 * «Даты рекламы» и называется «Параметры <система>», напр. «Параметры Яндекс.Директ».
 *
 * Ключи (они же id фильтра и dataKey группировки):
 *  - параметр метки — `utm_param_<source_id>_<utm_name_id>`
 *  - срез статистики Директа — `ads_dim_<срез>` (у секции «Яндекс.Директ» бэк отдаёт ТОЛЬКО их:
 *    девять срезов ТЗ `FRONTEND_ADS_DIMS_TZ.md`, ред. 3; см. `helpers/utils/adsDims.ts`).
 *    У срезов с коротким словарём (устройство, сеть, тип условия показа, формат, пол, возраст)
 *    значения приходят сразу в справочнике (`params[].items`) и показываются списком без поиска,
 *    как «Кабинет»; у площадки, региона и условия показа — обычный lazylist через /filter/list-values
 *  - кабинет  — `utm_cabinet_<source_id>`
 * Отчёт рекламы работает в неймспейсе регистраций без суффикса, поэтому dataKey и id фильтра
 * совпадают.
 */

import { getAdsDimDescription } from './adsDims';

/** Значение короткого словаря среза (`params[].items`), напр. «Женский» у пола */
export interface IUtmSectionItem {
    /** id значения словаря; 0 — «Не определено» */
    id: number | string;
    name: string;
}

/** Параметр рекламной системы из справочника */
export interface IUtmSectionParam {
    /** id параметра метки; у срезов Директа (`ads_dim_*`) — null */
    utm_name_id: number | null;
    /** Готовый ключ: `utm_param_<source_id>_<utm_name_id>` либо `ads_dim_<срез>` */
    key: string;
    /** Подпись пункта, напр. «Площадка» */
    name: string;
    /** Техническое имя параметра на бэке, напр. `placement` */
    utm_name?: string;
    /** Справочно: 1 — измерение, 2 — идентификатор, 3 — клик-метка. На тип фильтра не влияет */
    kind?: number;
    /** false — параметр не предлагать в группировках, только в фильтрах */
    groupable?: boolean;
    /**
     * Готовый словарь значений (ТЗ FRONTEND_ADS_DIMS_TZ, ред. 3): у срезов на 2–9 значений бэк
     * отдаёт их сразу, первым `{id: 0, name: «Не определено»}`. Нет поля — значения через
     * /filter/list-values по подстроке, как обычно
     */
    items?: IUtmSectionItem[];
}

/** Кабинет рекламной системы */
export interface IUtmSectionCabinet {
    id: number;
    name: string;
}

/** Секция — одна рекламная система школы */
export interface IUtmSection {
    source_id: number;
    /** Название секции, напр. «Яндекс.Директ» */
    source_name: string;
    /** Ключ пункта «Кабинет»: `utm_cabinet_<source_id>` */
    cabinet_key: string;
    params: IUtmSectionParam[];
    /** Кабинеты с разобранными строками. Пустой список — пункт «Кабинет» не показываем */
    cabinets: IUtmSectionCabinet[];
}

/** Префиксы динамических ключей — по ним узнаём «наши» фильтры и оси */
const UTM_PARAM_PREFIX = 'utm_param_';
const UTM_CABINET_PREFIX = 'utm_cabinet_';

/**
 * Разбор ключа оси. Регулярки 1:1 с бэковыми (`UniversalFilter::utmLevel()`): бэк допускает
 * суффикс отчёта `_2`/`_3`/`_4`, хотя фронт оси с суффиксом больше не строит. Суффикс нельзя
 * срезать «последним числом»: у кабинета Яндекс.Директа ключ `utm_cabinet_2`, где 2 — это
 * source_id. Поэтому разбираем структурно: параметр несёт два числа, кабинет — одно.
 */
const UTM_PARAM_KEY_RE = /^utm_param_(\d+)_(\d+)(?:_([234]))?$/;
const UTM_CABINET_KEY_RE = /^utm_cabinet_(\d+)(?:_([234]))?$/;

/**
 * ВРЕМЕННОЕ ограничение: на фронт выводим только «Яндекс.Директ» (source_id = 2).
 * Бэкенд отдаёт в справочнике все системы школы (Facebook ADS и т.д.), но показывать их пока
 * не нужно. Чтобы включить остальные — добавить source_id в список либо убрать фильтрацию
 * в `normalizeUtmSections`; больше нигде правок не требуется.
 */
const VISIBLE_UTM_SOURCE_IDS: number[] = [ 2 ];

/**
 * Пункты прежнего узла, которые бэк свёл к срезам (ТЗ FRONTEND_ADS_DIMS_TZ, п. 4): Площадка →
 * `ads_dim_placement`, Регион → `ads_dim_region`, ID региона → `ads_dim_region_id`, Тип устройства →
 * `ads_dim_device`, Тип площадки → `ads_dim_network`, ID фразы → `ads_dim_criteria`. Новый бэк их
 * уже не отдаёт, страховка от старого; сохранённые отчёты со старыми ключами бэк продолжает понимать.
 */
const SUPERSEDED_UTM_PARAM_KEYS: string[] = [
    'utm_param_2_16',
    'utm_param_2_17',
    'utm_param_2_18',
    'utm_param_2_19',
    'utm_param_2_21',
    'utm_param_2_24',
];

/** Единственный отчёт, где показываем параметры Яндекс.Директ (фильтры и группировки) */
export const UTM_SECTIONS_REPORT_PATHNAME = '/users/ad-reports';

/** Сущность «Реклама»: в дереве фильтров ищем по id, в дереве группировок — по имени */
const AD_ENTITY_NAME = 'Реклама';
const AD_FILTER_ENTITY_ID = 'ads_filter';
/** Узел «Дата рекламы» — секции встают сразу после него */
const AD_DATE_FILTER_ID = 'ads_data';
const AD_DATE_GROUPING_NAME = 'Дата рекламы';

/** Имя узла секции: «Параметры Яндекс.Директ» */
const sectionNodeName = (section: IUtmSection): string => `Параметры ${section.source_name}`;

const FILTER_SECTION_DESCRIPTION =
    'Срезы статистики рекламной системы: расход, клики и показы по площадке, региону, устройству, сети, '
    + 'условию показа и его типу, формату объявления, полу и возрасту; регистрации — по UTM-метке.';
const GROUPINGS_SECTION_DESCRIPTION =
    'Группировка по срезам статистики рекламной системы (расход, клики, показы) '
    + 'и параметрам из UTM-меток регистрации.';

/** Значение словаря валидно, если у него есть id и непустое имя */
const isValidSectionItem = (item: any): boolean =>
    Boolean(item)
    && (typeof item.id === 'number' || typeof item.id === 'string')
    && typeof item.name === 'string'
    && item.name.length > 0;

/**
 * Пункт секции как пришёл, но со словарём `items` только если он непустой и валидный:
 * пустой/битый словарь — это обычный lazylist по подстроке.
 */
const normalizeSectionParam = (param: any): IUtmSectionParam => {
    const { items, ...rest } = param;
    const validItems: IUtmSectionItem[] = Array.isArray(items)
        ? items.filter(isValidSectionItem).map((item: any): IUtmSectionItem => ({ id: item.id, name: item.name }))
        : [];
    return validItems.length ? { ...rest, items: validItems } : rest;
};

/**
 * Приводит ответ справочника к массиву валидных секций.
 * Секция без ключей или без параметров в дерево не попадает; системы вне
 * `VISIBLE_UTM_SOURCE_IDS` отсеиваются (временно показываем только «Яндекс.Директ»).
 */
export const normalizeUtmSections = (raw: unknown): IUtmSection[] => {
    if (!Array.isArray(raw)) return [];

    return raw
        .filter((section: any) =>
            section
            && VISIBLE_UTM_SOURCE_IDS.includes(Number(section.source_id))
            && typeof section.source_name === 'string'
            && section.source_name.length > 0
            && Array.isArray(section.params)
            && section.params.length > 0
        )
        .map((section: any): IUtmSection => ({
            source_id: section.source_id,
            source_name: section.source_name,
            cabinet_key: section.cabinet_key ?? `${UTM_CABINET_PREFIX}${section.source_id}`,
            params: (section.params as any[])
                .filter(
                    (param) => param
                        && typeof param.key === 'string'
                        && typeof param.name === 'string'
                        && !SUPERSEDED_UTM_PARAM_KEYS.includes(param.key)
                )
                .map((param): IUtmSectionParam => normalizeSectionParam(param)),
            cabinets: Array.isArray(section.cabinets) ? section.cabinets : [],
        }))
        .filter((section) => section.params.length > 0);
};

/**
 * Узлы секций для дерева ФИЛЬТРОВ. Пункты того же вида, что «UTM source регистрации»:
 * lazylist со значениями из /filter/list-values и плюсиком «добавить из строки отчёта».
 * В фильтры идут ВСЕ параметры секции (`groupable` ограничивает только группировки).
 */
export const buildUtmFilterNodes = (sections: IUtmSection[]): any[] =>
    sections.map((section) => ({
        name: sectionNodeName(section),
        // id контейнера — в запрос не уходит, нужен только для ключей дерева
        id: `utm_section_${section.source_id}`,
        visibleDescription: true,
        description: FILTER_SECTION_DESCRIPTION,
        children: [
            ...section.params.map((param) => {
                const description = getAdsDimDescription(param.key);
                return {
                    name: param.name,
                    full_name: `${param.name} (${section.source_name})`,
                    id: param.key,
                    type: 'lazylist',
                    showListPlus: true,
                    ...(description ? { description } : {}),
                    // Короткий словарь среза пришёл в справочнике — показываем сразу, как кабинеты.
                    // Тип остаётся lazylist: пейлоад фильтра и поиск по подстроке не меняются;
                    // id — строкой, пикер сравнивает значения через String(id)
                    ...(param.items?.length
                        ? { presetItems: param.items.map((item) => ({ id: String(item.id), name: item.name })) }
                        : {}),
                };
            }),
            ...(section.cabinets.length
                ? [ {
                    name: 'Кабинет',
                    full_name: `Кабинет (${section.source_name})`,
                    id: section.cabinet_key,
                    type: 'lazylist',
                    showListPlus: true,
                    // Кабинетов единицы, и они уже пришли в справочнике — показываем их сразу,
                    // не заставляя пользователя угадывать подстроку для поиска.
                    // Тип остаётся lazylist: контракт запроса к бэку не меняется, поиск работает.
                    presetItems: section.cabinets.map((cabinet) => ({
                        id: String(cabinet.id),
                        name: cabinet.name,
                    })),
                } ]
                : []),
        ],
    }));

/**
 * Встраивает секции в дерево ФИЛЬТРОВ — в сущность «Реклама» сразу после «Даты рекламы».
 * Вход НЕ мутируется: клонируется только изменяемая ветка.
 * Пустой справочник возвращает исходное дерево (узла-заглушки нет).
 */
export const injectUtmSectionsIntoFilters = (data: any[], sections?: IUtmSection[]): any[] => {
    const validSections = normalizeUtmSections(sections);
    if (!Array.isArray(data) || !validSections.length) return data;

    const nodes = buildUtmFilterNodes(validSections);
    const sectionIds = new Set(nodes.map((node) => node.id));

    return data.map((entity: any) => {
        if (entity?.id !== AD_FILTER_ENTITY_ID || !Array.isArray(entity.children)) {
            return entity;
        }
        // Повторный вызов не должен плодить дубли (дерево строится на каждый рендер)
        const children = entity.children.filter((child: any) => !sectionIds.has(child?.id));
        const dateIndex = children.findIndex((child: any) => child?.id === AD_DATE_FILTER_ID);
        const insertAt = dateIndex >= 0 ? dateIndex + 1 : 0;

        return {
            ...entity,
            children: [
                ...children.slice(0, insertAt),
                ...nodes,
                ...children.slice(insertAt),
            ],
        };
    });
};

/**
 * Узлы секций для дерева ГРУППИРОВОК. В группировки идут только `groupable` параметры.
 * dataKey без суффикса: отчёт рекламы работает в неймспейсе регистраций.
 */
export const buildUtmGroupingsNodes = (sections: IUtmSection[]): any[] =>
    sections
        .map((section) => {
            const children = [
                ...section.params
                    .filter((param) => param.groupable !== false)
                    .map((param) => {
                        const description = getAdsDimDescription(param.key);
                        return {
                            name: param.name,
                            dataKey: param.key,
                            ...(description ? { description } : {}),
                        };
                    }),
                ...(section.cabinets.length
                    ? [ { name: 'Кабинет', dataKey: section.cabinet_key } ]
                    : []),
            ];
            return {
                name: sectionNodeName(section),
                isUtmSectionNode: true,
                description: GROUPINGS_SECTION_DESCRIPTION,
                children,
            };
        })
        // Секция, у которой все параметры неgroupable и нет кабинетов, в группировках не нужна
        .filter((node) => node.children.length > 0);

/**
 * Встраивает секции в дерево ГРУППИРОВОК — в сущность «Реклама» сразу после «Даты рекламы».
 * Работает как `injectRegistrationGroupsNode`: мутирует переданное дерево,
 * поэтому вызывать на клоне (все точки вызова уже клонируют).
 */
export const injectUtmSectionsNode = (data: any[], sections?: IUtmSection[]): any[] => {
    const validSections = normalizeUtmSections(sections);
    if (!Array.isArray(data) || !validSections.length) return data;

    const nodes = buildUtmGroupingsNodes(validSections);
    if (!nodes.length) return data;

    const sectionNames = new Set(nodes.map((node) => node.name));
    const adEntity = data.find((item: any) => item?.name === AD_ENTITY_NAME);

    if (adEntity && Array.isArray(adEntity.children)) {
        // Повторный вызов не должен плодить дубли
        adEntity.children = adEntity.children.filter(
            (child: any) => !(child?.isUtmSectionNode && sectionNames.has(child?.name))
        );
        const dateIndex = adEntity.children.findIndex(
            (child: any) => child?.name === AD_DATE_GROUPING_NAME
        );
        adEntity.children.splice(dateIndex >= 0 ? dateIndex + 1 : 0, 0, ...nodes);
    }

    return data;
};

/**
 * Ключ оси разобранной UTM-метки → id фильтра. Обычная мапа не подходит: ключи динамические.
 * Правило из ТЗ: id фильтра равен dataKey без суффикса отчёта (суффикс допускаем на случай
 * сохранённых ранее осей `_2`/`_3`/`_4`).
 * @returns id фильтра либо undefined, если dataKey не относится к разобранным меткам
 */
export const resolveUtmFilterId = (dataKey?: string): string | undefined => {
    if (typeof dataKey !== 'string') return undefined;

    const paramMatch = dataKey.match(UTM_PARAM_KEY_RE);
    if (paramMatch) {
        return `${UTM_PARAM_PREFIX}${paramMatch[1]}_${paramMatch[2]}`;
    }

    const cabinetMatch = dataKey.match(UTM_CABINET_KEY_RE);
    if (cabinetMatch) {
        return `${UTM_CABINET_PREFIX}${cabinetMatch[1]}`;
    }

    return undefined;
};
